import { DateTime, FixedOffsetZone } from 'luxon';
import { formatWeekdayDatePadded, formatWeekdayDatePaddedWithYear } from './date-format';

// Convert ISO time string ("HH:mm" format) to number of hours (with decimals).

export function isoTimeToHours(iso: string): number {
  let parsed = iso.match(/(..):(..)/) || ['', '00', '00'];

  return parseInt(parsed[1]) + parseInt(parsed[2]) / 60;
}
// Set particular hour (with decimals!) in a datetime

export function setDecimalHour(time: DateTime, hourDecimal: number): DateTime {
  if (isNaN(hourDecimal)) {
    console.log('setDecimalHour called with NaN hourDecimal');
    return time;
  }
  let hour = Math.floor(hourDecimal);
  let minute = Math.round((hourDecimal - hour) * 60);

  return time.set({ hour, minute });
}/** WARNING! Luxon uses ISO weekday indices (sunday=7), whereas native JS DateTime uses US-locale (sunday=0). */


export enum ISOWeekDayNumber {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 7
}
function nextify(unit: keyof DateTime, nextUnit: string) {
  return (dt: DateTime, value: number) => (dt.get(unit) >= value ? dt.plus({ [nextUnit]: 1 }) : dt).set({ [unit]: value });
}

export const nextMonth = nextify('month', 'year');
export const nextWeekday = nextify('weekday', 'week');
export function RoundTimeToMinutes(time: DateTime, roundMinutes: number): DateTime {
  let result = time.endOf('minute');
  let minutes = result.minute;
  minutes = Math.round(minutes / roundMinutes) * roundMinutes;

  if (minutes >= 60) {
    result = result.plus({ hours: 1 }).set({ minute: 0 });
  } else {
    result = result.set({ minute: minutes });
  }

  return result;
}
//  Convert number of hours (with decimals) toISO time string ("HH:mm" format)

export function hoursToIsoTime(hours: number): string {
  let h = Math.floor(hours);
  let m = Math.round((hours - h) * 60);

  return ('0' + h.toFixed(0)).slice(-2) + ':' + ('0' + m.toFixed(0)).slice(-2);
}
/**
 * The three near-day words `relativeDayLabel` needs.
 *
 * Shared code cannot import the client's generated `TranslationFunctions`
 * type, so the caller hands in just this small slice of it. The client passes
 * `LL.common.relativeDay`, whose members are exactly these three no-argument
 * functions; tests can pass a plain object. Passing it EXPLICITLY (rather than
 * reading a hidden global) matches how `locale` is already threaded through
 * every helper in shared/src/date-format.ts.
 */
export interface RelativeDayTexts {
  today: () => string;
  yesterday: () => string;
  tomorrow: () => string;
}

/** Generate a human-readable label for a particular date. Outputs `today`, `tomorrow`, `yesterday`,
 * or a short date (without year), or full date (with year), depending on when that date is
 * relative to provided "today" value.
 */

export function relativeDayLabel(today: DateTime, time: DateTime, locale: string, texts: RelativeDayTexts) {
  let timeBOD = time.startOf('day');
  let todayBOD = today.startOf('day');
  let todayDelta = timeBOD.diff(todayBOD, 'days').days;
  // the near-day words come from the caller's translation object (#4317); the
  // date branches below localize through the `locale` argument instead
  if (todayDelta == 0) {
    return texts.today();
  } else if (todayDelta == -1) {
    return texts.yesterday();
  } else if (todayDelta == +1) {
    return texts.tomorrow();
  } else if (timeBOD.hasSame(todayBOD, 'year')) {
    // same-year: weekday + month name + day, names/order follow the UI language
    return formatWeekdayDatePadded(timeBOD, locale);
  } else {
    // other year: include the year as well
    return formatWeekdayDatePaddedWithYear(timeBOD, locale);
  }
}


/** Calculate difference in days between two dates, ignoring time of day.
 *
 * The concept of "ignoring time of day" only makes sense when used with dates that are
 * both in the same timezone - either local or some other tz (i.e. when the server is
 * calculating something on behalf of the client). It makes no sense in UTC, because
 * UTC doesn't have a "morning" or "evening" - it's just an absolute time coordinate.
 *
 * Therefore, the function will throw if the dates are not in the same timezone, or if they are UTC dates.
 * 
 * None of these situations should ever happen in the app, because this is an internal function and we
 * use type-safe wrappers that prevent it from being called incorrectly.
 *
 */
// function diffInDays_internal(a: DateTime, b: DateTime): number {
//   if (a.zoneName !== b.zoneName) {
//     throw Error('diffInDays() cannot be used with dates in different timezones!');
//   }
//   if (a.zoneName === 'UTC') {
//     throw Error('diffInDays() cannot be used with UTC dates!');
//   }
//   return a.startOf('day').diff(b.startOf('day'), 'days').days;
// }

// Base wrapper for Luxon DateTime. Do not use directly!
class Time_Base {
  dt: DateTime;
  type: string; // this is just to prevent "sidewise" conversions of derived types, which TypeScript otherwise allows if all the members are the same.
  constructor(dt: DateTime) {
    this.dt = dt;
  }
  toISO(): string {
    return this.dt.toISO();
  }
};

/// Time in UTC zone.
export class TimeUTC extends Time_Base {
  type: 'utc' = 'utc';
  constructor(dt: DateTime) {
    if (dt.zoneName !== 'UTC') {
      throw Error('TimeUTC() must be constructed with a UTC DateTime!');
    }
    super(dt);
  }

  /** Parse an ISO DateTime string in UTC, and return a DateTime object.
   * This will throw if the string is not a valid ISO DateTime OR if it is not explicitly marked as being in UTC.
   */
  static fromISO(str: string): TimeUTC {
    let parsed = DateTime.fromISO(str, { setZone: true });
    if (!parsed.isValid) {
      throw Error(`Invalid ISO DateTime string "${str}"`);
    }
    if (parsed.zoneName !== 'UTC') {
      throw Error(`ISO DateTime string "${str}" is not in UTC.`);
    }
    return new TimeUTC(parsed);
  }
};

/// Time in local zone.
// export class TimeLocal extends Time_Base {
//   type: 'local' = 'local';
//   constructor(dt: DateTime) {
//     if (dt.zone.type !== 'local') {
//       throw Error('TimeLocal() must be constructed with a local DateTime!');
//     }
//     super(dt);
//   }

//   /** Parse an ISO DateTime string in local TZ (no offset suffix), and return a DateTime object.
//    * This will throw if the string is not a valid ISO DateTime OR if it has an offset.
//    */
//   static fromISO(str: string): TimeLocal {
//     let parsed = DateTime.fromISO(str, { setZone: true });
//     if (!parsed.isValid) {
//       throw Error(`Invalid ISO DateTime string "${str}"`);
//     }
//     if (parsed.zone.type !== 'local') {
//       throw Error(`ISO DateTime string "${str}" is not in local time zone.`);
//     }
//     return new TimeLocal(parsed);
//   }

//   static diffInDays(a: TimeLocal, b: TimeLocal): number {
//     return diffInDays_internal(a.dt, b.dt);
//   }

// };

/// Time in a custom zone.
// export class TimeTZ extends Time_Base {
//   type: 'tz' = 'tz';
//   constructor(dt: DateTime) {
//     if (dt.zone.type === 'local' || dt.zoneName === 'UTC') {
//       throw Error('TimeTZ() must be constructed with a non-local, non-UTC DateTime!');
//     }
//     super(dt);
//   }
//   /** Parse an ISO DateTime string that specifies a TZ, and return a DateTime object.
//    * This will throw if the string is not a valid ISO DateTime OR if it doens't have a specified offset.
//    */
//   static fromISO(str: string): TimeTZ {
//     let parsed = DateTime.fromISO(str, { setZone: true });
//     if (!parsed.isValid) {
//       throw Error(`Invalid ISO DateTime string "${str}"`);
//     }
//     if (parsed.zone.type === 'local' || parsed.zoneName === 'UTC') {
//       throw Error(`ISO DateTime string "${str}" does not specify a timezone.`);
//     }

//     return new TimeTZ(parsed);
//   }

//   static diffInDays(a: TimeTZ, b: TimeTZ): number {
//     return diffInDays_internal(a.dt, b.dt);
//   }
// };

export function getLocalOffsetTimeInDesiredTimeZone(time_utc: DateTime, localTimeOffsetMin, desiredTime: DateTime): DateTime {
  return time_utc.setZone(FixedOffsetZone.instance(localTimeOffsetMin)).setZone(desiredTime.zone, { keepLocalTime: true });
}

